import React, { useCallback, useMemo } from 'react';
import { toast as hotToast } from 'react-hot-toast';
import type { UseToastOptions, UseToastReturn } from './Toast.types';
import { StyledMessage } from './Toast.styles';
import { TOAST_DESTRUCTIVE_CLASS } from './Toast.constants';

export const useToast = (): UseToastReturn => {
  const toast = useCallback((message: React.ReactNode, options?: UseToastOptions) => {
    // `intent` is ours, not react-hot-toast's — translate it to the class the container reads, and
    // keep it out of the options we hand down.
    const { intent, ...hotToastOptions } = options ?? {};
    return hotToast(<StyledMessage data-testid="toast-message">{message}</StyledMessage>, {
      ...hotToastOptions,
      ...(intent === 'destructive'
        ? {
            className: TOAST_DESTRUCTIVE_CLASS,
            // react-hot-toast stamps every toast `role="status" aria-live="polite"`. A polite region
            // queues behind whatever is being read and is dropped outright if the node goes away
            // first — which, at a 3s auto-dismiss, is the normal case rather than the edge one. A
            // destructive toast is often the only feedback for a blocked action, so it interrupts.
            ariaProps: { role: 'alert' as const, 'aria-live': 'assertive' as const },
          }
        : null),
    });
  }, []);

  const dismiss = useCallback((id: string) => {
    hotToast.dismiss(id);
  }, []);

  const removeAll = useCallback(() => {
    hotToast.dismiss();
  }, []);

  return useMemo(() => ({ toast, dismiss, removeAll }), [toast, dismiss, removeAll]);
};
